import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
  DestroyRef,
} from '@angular/core';
import { MaterialModule } from '../material/material.module';
import {
  DocumentSharing,
  DocumentSharingControllerService,
} from '../../core/generated';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { ProfileProviderPJCacheListService } from '../services/profile-provider-pj-cache-list.service';
import { UserRefCacheListService } from '../services/user-ref-cache-list.service';
import { CommonModule } from '@angular/common';
import { CnpjMaskPipe } from '../pipes/cnpj-mask.pipe';
import { CpfMaskPipe } from '../pipes/cpf-mask.pipe';
import { MatDialog } from '@angular/material/dialog';
import { DocumentListDialogComponent } from '../document-list-dialog/document-list-dialog.component';
import { MatTableDataSource } from '@angular/material/table';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-document-sharing',
    standalone: true,
    imports: [MaterialModule, CommonModule, CnpjMaskPipe, CpfMaskPipe],
    templateUrl: './document-sharing.component.html',
    styleUrl: './document-sharing.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentSharingComponent implements OnInit, OnChanges {
  private readonly documentSharingService = inject(DocumentSharingControllerService);
  private readonly userRefCacheListService = inject(UserRefCacheListService);
  private readonly profileProviderPJCacheListService = inject(ProfileProviderPJCacheListService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  @Input() granted?: string;
  @Input() owner?: string;
  @Input() add?: DocumentSharing;

  readonly dataSource = new MatTableDataSource<DocumentSharing>([]);

  // Cache local para evitar múltiplas chamadas ao serviço
  private readonly userCacheRequested = new Set<string>();
  private readonly companyCacheRequested = new Set<string>();

  get documentSharing(): DocumentSharing[] {
    return this.dataSource.data;
  }

  set documentSharing(value: DocumentSharing[]) {
    this.dataSource.data = value;
  }

  displayedColumns: string[] = [
    'id',
    'ownerDocumentsCpfCnpj',
    'grantedCompanyCnpj',
    'grantedDate',
    'revokedDate',
    'active',
    'actions',
  ];

  ngOnInit(): void {
    this.setupDisplayedColumns();
    this.loadDocumentSharing();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['add']?.currentValue) {
      this.handleNewDocumentSharing(changes['add'].currentValue as DocumentSharing);
    }
  }

  private setupDisplayedColumns(): void {
    if (this.granted && !this.owner) {
      this.displayedColumns = [
        'ownerDocumentsCpfCnpj',
        'grantedDate',
        'revokedDate',
        'active',
        'actions',
      ];
    } else if (!this.granted && this.owner) {
      this.displayedColumns = [
        'grantedCompanyCnpj',
        'grantedDate',
        'revokedDate',
        'active',
        'actions',
      ];
    }
  }

  private handleNewDocumentSharing(newDocumentSharing: DocumentSharing): void {
    const exists = this.dataSource.data.some(item =>
      item.id === newDocumentSharing.id ||
      (item.grantedCompanyCnpj === newDocumentSharing.grantedCompanyCnpj &&
        item.ownerDocumentsCpfCnpj === newDocumentSharing.ownerDocumentsCpfCnpj)
    );

    if (!exists) {
      this.dataSource.data = [newDocumentSharing, ...this.dataSource.data];
      this._snackBar.open('Compartilhamento criado com sucesso!', 'Fechar', {
        duration: 3000,
      });
      this.cdr.markForCheck();
    }
  }

  async loadDocumentSharing(): Promise<void> {

    let data: DocumentSharing[] = [];

    if (!this.granted && !this.owner) {
      data = await firstValueFrom(this.documentSharingService.getAll());
    } else if (!this.granted && this.owner) {
      data = await firstValueFrom(this.documentSharingService.getGrantedFrom(this.owner));
    } else if (this.granted && !this.owner) {
      data = await firstValueFrom(this.documentSharingService.getGrantedTo(this.granted));
    }

    this.dataSource.data = data;

    // Pré-carrega dados de usuários e empresas
    if (data && data.length > 0) {
      this.preloadCacheData(data);
    }

    this.cdr.markForCheck();

  }

  private preloadCacheData(data: DocumentSharing[]): void {
    // Pré-carrega CPFs
    data.forEach(item => {
      if (item.ownerDocumentsCpfCnpj && item.ownerDocumentsCpfCnpj.length === 11) {
        this.prefetchUserData(item.ownerDocumentsCpfCnpj);
      } else if (item.ownerDocumentsCpfCnpj && item.ownerDocumentsCpfCnpj.length === 14) {
        this.prefetchCompanyData(item.ownerDocumentsCpfCnpj);
      }

      if (item.grantedCompanyCnpj) {
        this.prefetchCompanyData(item.grantedCompanyCnpj);
      }
    });
  }

  private prefetchUserData(cpf: string): void {
    if (!cpf || this.userCacheRequested.has(cpf)) return;

    this.userCacheRequested.add(cpf);
    this.userRefCacheListService.findUserRefByCpf(cpf, () => {
      this.cdr.markForCheck();
    });
  }

  private prefetchCompanyData(cnpj: string): void {
    if (!cnpj || this.companyCacheRequested.has(cnpj)) return;

    this.companyCacheRequested.add(cnpj);
    this.profileProviderPJCacheListService.findProfileProviderPJByCnpj(cnpj, () => {
      this.cdr.markForCheck();
    });
  }

  getUserName(cpf: string): string {
    const user = this.userRefCacheListService.findUserRefByCpf(cpf);

    return `${user?.firstName} ${user?.lastName || ''}`.trim();
  }

  getUserCertified(cpf: string): boolean {
    if (!cpf) return false;

    const user = this.userRefCacheListService.findUserRefByCpf(cpf);
    return user?.certifiedHubMedical || false;
  }

  getCompanyName(grantedCompanyCnpj: string): string {
    const pj =
      this.profileProviderPJCacheListService.findProfileProviderPJByCnpj(
        grantedCompanyCnpj
      );
    return pj?.companyName ?? 'N/A';
  }

  getCompanyCertified(cnpj: string): boolean {
    if (!cnpj) return false;

    const company = this.profileProviderPJCacheListService.findProfileProviderPJByCnpj(cnpj);
    return company?.certifiedHubMedical || false;
  }

  toggleActive(element: DocumentSharing): void {
    const updatedElement: DocumentSharing = {
      ...element,
      active: !element.active
    };

    this.documentSharingService
      .update13(updatedElement)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.dataSource.data = this.dataSource.data.map((item) =>
            item.id === element.id ? { ...item, ...result } : item
          );
          this.cdr.markForCheck();
        },
        error: () => {
          this._snackBar.open(
            'Erro ao atualizar status de compartilhamento',
            'Fechar',
            { duration: 5000 }
          );
        },
      });
  }

  deleteDocumentSharing(id: number): void {
    this.documentSharingService
      ._delete(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.dataSource.data = this.dataSource.data.filter((item) => item.id !== id);
          this.cdr.markForCheck();
        },
        error: () => {
          this._snackBar.open('Erro ao excluir compartilhamento', 'Fechar', {
            duration: 5000,
          });
        },
      });
  }

  seeFilesList(documentSharing: DocumentSharing): void {
    this.dialog.open(DocumentListDialogComponent, {
      width: '600px',
      data: {
        ownerDocumentsCpfCnpj: documentSharing.ownerDocumentsCpfCnpj,
        grantedCompanyCnpj: documentSharing.grantedCompanyCnpj,
      }
    });
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    const parent = img.parentElement;

    if (parent) {
      img.style.display = 'none';
      const fallbackIcon = document.createElement('mat-icon');
      fallbackIcon.textContent = img.src.includes('ausente') ? 'cancel' : 'verified';
      fallbackIcon.className = 'certified-icon';
      fallbackIcon.setAttribute('matTooltip', img.alt || '');
      parent.appendChild(fallbackIcon);
    }
  }

  trackByDocumentSharing(index: number, item: DocumentSharing): number {
    return item.id || index;
  }
}
